import { staleTimes, useQuery } from '@platform/api-client';
import { browserTimeZone, type ChartPoint } from '@platform/design-system';
import type { Operation, QueryParams, ResponseBody, TypedClient } from '@platform/openapi';
import { countPointOf, moneyPointOf } from '../chart-points.ts';
import type { ReportPeriod, SeriesUnit } from '../report-period.ts';
import { queryKeys, usePageContext } from '../usePageContext.ts';

export type PerformanceReport = ResponseBody<Operation<'/admin/dashboard/performance', 'get'>>;
export type TopProduct = PerformanceReport['topProducts'][number];
export type PerformanceSummary = Pick<
  PerformanceReport,
  'orders' | 'sales' | 'units' | 'returnedValue' | 'damagedUnits' | 'charges' | 'profit' | 'series'
>;
export type Profit = NonNullable<PerformanceReport['profit']>;

export const CHART_METRICS = ['created', 'delivered', 'sales'] as const;
export type ChartMetric = (typeof CHART_METRICS)[number];

export type SeriesMetric = Exclude<keyof PerformanceReport['series'][number], 'start'>;

export type ProductRanking = NonNullable<PerformanceQuery['topBy']>;

export type TopProductsRequest = { readonly rankedBy: ProductRanking; readonly size: number };

type PerformanceQuery = NonNullable<QueryParams<Operation<'/admin/dashboard/performance', 'get'>>>;

export type Comparison = NonNullable<PerformanceQuery['compare']>;

export type ProductFilter = { readonly productId: string | null; readonly origin: string | null };

export const EVERY_PRODUCT: ProductFilter = { productId: null, origin: null };

export const isProductFiltered = (filter: ProductFilter): boolean =>
  filter.productId !== null || filter.origin !== null;

export const productFilterQueryOf = (filter: ProductFilter) => ({
  ...(filter.productId === null ? {} : { product_id: filter.productId }),
  ...(filter.origin === null ? {} : { origin: filter.origin }),
});

export type PerformanceRequest = {
  readonly api: TypedClient;
  readonly tenantId: string;
  readonly period: ReportPeriod;
  readonly unit: SeriesUnit;
  readonly company: string | null;
  readonly productFilter?: ProductFilter;
  readonly topProducts?: TopProductsRequest;
  readonly comparison?: Comparison;
};

const topProductsQueryOf = (topProducts: TopProductsRequest | undefined) =>
  topProducts === undefined ? {} : { topBy: topProducts.rankedBy, topSize: topProducts.size };

export const comparisonQueryOf = (comparison: Comparison | undefined) =>
  comparison === undefined || comparison === 'none' ? {} : { compare: comparison };

export const performanceQueryOf = ({
  api,
  tenantId,
  period,
  unit,
  company,
  productFilter = EVERY_PRODUCT,
  topProducts,
  comparison,
}: PerformanceRequest) => {
  const query = {
    ...period,
    unit,
    timeZone: browserTimeZone(),
    ...(company === null ? {} : { company }),
    ...productFilterQueryOf(productFilter),
    ...topProductsQueryOf(topProducts),
    ...comparisonQueryOf(comparison),
  };
  return {
    queryKey: queryKeys.performance(tenantId, query),
    queryFn: () => api.get('/admin/dashboard/performance', { query }),
    staleTime: staleTimes.dashboard,
  };
};

export const usePerformance = (
  api: TypedClient,
  period: ReportPeriod,
  unit: SeriesUnit,
  company: string | null,
) => {
  const { tenantId } = usePageContext();
  return useQuery(performanceQueryOf({ api, tenantId, period, unit, company }));
};

export const deliveryRateOf = (
  orders: Pick<PerformanceReport['orders'], 'delivered' | 'returned'>,
): number | null => {
  const settled = orders.delivered + orders.returned;
  return settled === 0 ? null : orders.delivered / settled;
};

export const chartPointsOf = (
  report: PerformanceReport,
  metric: SeriesMetric,
  locale: string,
): ChartPoint[] => {
  const scale = { unit: report.unit, timeZone: report.timeZone, locale };
  return report.series.map((point) =>
    metric === 'sales'
      ? moneyPointOf(point.start, { amount: point.sales, currency: report.currency }, scale)
      : countPointOf(point.start, point[metric], scale),
  );
};
